import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SectionTitle from './SectionTitle';
import PresetGroupCard from './PresetGroupCard';
import PresetGroupCardPacks from './PresetGroupCardPacks';
import PresetGroupCardQuad from './PresetGroupCardQuad';
import HomeSeeAllCard from './HomeSeeAllCard';
import HorizontalScrollRow from '../catalog/HorizontalScrollRow';
import {
  buildSectionRowItems,
  isSeeAllRowItem,
} from '../catalog/sectionRowItems';
import { seedPresetGroup } from '../../core/queries/presetGroups';
import { localizedField } from '../../locales/content';

/**
 * HomeSection — одна секция Home: заголовок + горизонтальный ряд карточек.
 *
 * Composition: SectionTitle + (PresetGroupCard | PresetGroupCardPacks).
 * Both sub-components now compose <Cover> primitive (see Section C7 retrofit)
 * — no invented styles remain in this layer. HomeSection itself only does
 * layout (flex column, gap tokens) and routing decision.
 *
 * Two supported call-sites:
 *  1. Home (stage 02): `section.refData = group` — рендерим одну карточку.
 *     Это исторический API, используемый HomePage (section.refData — object).
 *  2. Photo catalog (stage 03): `section.items = [{id, cover, isNew, type, name}]`
 *     — рендерим N карточек в горизонтальном ряду под одним заголовком
 *     (figma 318:4525 — section = title + horizontal-scroll row of covers).
 *     Закрывает stage-03 MAJOR B2 (до фикса PhotoCatalog разворачивал каждую
 *     catalog-секцию в N pseudo-HomeSection-ов с empty title-ами).
 *
 * Edge cases (acceptance §2.4 + drift §3):
 *  - sectionType === 'VIDEO' → null (video-секции приедут стадией 05)
 *  - refData === null И items пуст/отсутствует → null (graceful skip)
 *  - displayMode === 'shuffle' → PresetGroupCardPacks
 *  - displayMode === 'quad'    → PresetGroupCardQuad (2×2 first-four preview)
 *  - else                      → PresetGroupCard
 *
 * Навигация по tap:
 *  - presets.length > 1 → /group/:id
 *  - presets.length === 1 → /prompt/:pid
 *  - items без `presets` (catalog shape) → navigate(`/group/:id`) (стадия 03,
 *    figma-spec §3.5: multi-preset group → /group/:id; single-preset branch
 *    не встречается в catalog payload — там presets не приходит).
 *
 * Props:
 *   section — {
 *     title, isNew?, sectionType, displayMode?,
 *     refData?  — single group (Home shape), либо
 *     items?    — array of groups (catalog shape)
 *   }
 */
export default function HomeSection({ section }) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  if (!section) return null;
  if (section.sectionType === 'VIDEO') return null;

  const hasItems = Array.isArray(section.items) && section.items.length > 0;
  if (!section.refData && !hasItems) return null;

  const { isNew, displayMode } = section;
  const title = localizedField(section, 'title', {
    fallbackSeed: section.refId || section.slug || '',
  });

  const handleClick = (g) => {
    const presets = g.presets || [];
    if (presets.length === 1) {
      navigate(`/prompt/${presets[0].id}`);
    } else {
      seedPresetGroup(g);
      navigate(`/group/${g.id}`, { state: { group: g } });
    }
  };

  const Card =
    displayMode === 'shuffle'
      ? PresetGroupCardPacks
      : displayMode === 'quad'
        ? PresetGroupCardQuad
        : PresetGroupCard;

  const groups = hasItems ? section.items : [section.refData];
  const canSeeAll = hasItems && Boolean(section.id) && Boolean(section.refId);
  const rowItems = buildSectionRowItems(groups, {
    canSeeAll,
    markerId: section.id,
  });

  return (
    <section className="flex flex-col gap-gap-title-cards">
      <div className="flex items-center justify-between gap-3">
        <SectionTitle title={title} isNew={isNew} />
        {canSeeAll && (
          <button
            type="button"
            onClick={() => navigate(`/photo-catalog/section/${section.id}`)}
            className="flex shrink-0 items-center gap-2 font-sans text-sm font-semibold text-text-primary"
            data-testid="home-section-see-all"
          >
            <span>{t('common.seeAll', 'See all')}</span>
            <span aria-hidden className="text-[20px] leading-none">→</span>
          </button>
        )}
      </div>
      <HorizontalScrollRow
        data-testid="home-section-row"
        className="flex flex-row items-stretch gap-gap-cards overflow-x-auto scrollbar-hide -mx-4 px-4 cursor-grab active:cursor-grabbing"
        items={rowItems}
        itemWidth={(group) => (isSeeAllRowItem(group) ? 80 : 160)}
        initialItemCount={3}
        overscan={0}
        placeholderHeight={160}
        deferUntilVisible
        visibilityRootMargin="0px 0px -180px 0px"
        renderItem={(group) =>
          isSeeAllRowItem(group) ? (
            <HomeSeeAllCard onClick={() => navigate(`/photo-catalog/section/${section.id}`)} />
          ) : (
            <Card group={group} onClick={handleClick} />
          )
        }
      >
        {null}
      </HorizontalScrollRow>
    </section>
  );
}
